import { useCallback, useEffect, useMemo, useState } from 'react';
import { type MapMouseEvent, Popup } from 'react-map-gl/mapbox';
import TrailCard from '@alltrails/cards/components/TrailCard';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import type { TrailHit } from '@alltrails/types/AlgoliaResults';
import type ListItem from '@alltrails/lists/types/ListItem';
import useHoveredAdminResult from '../../hooks/useHoveredAdminResult';
import useMap from '../../hooks/useMap';
import { useGetPolylineQuery } from '../../redux/mapsApi';
import Polyline from '../Polyline';
import ActivityCard from './ActivityCard';
import MapCard from './MapCard';
import { AdminMapHit, AdminTrackHit, trailHitToTrail } from './utils';

export type ListItemsProps = {
  isFavorite: (itemType: ListItem['type'], itemId: number) => boolean;
  onFavoriteClick: (itemType: ListItem['type'], itemId: number) => void;
  listModal: React.ReactNode;
  signUpModal: React.ReactNode;
};

type Props = {
  listItemsProps?: ListItemsProps;
};

const HoveredResultCardAndPolyline = ({ listItemsProps }: Props) => {
  const baseUrl = usePublicBaseUrl();
  const map = useMap();
  const hoveredAdminResult = useHoveredAdminResult();

  const { hasPermission } = useAuthorization();
  const isAdmin = hasPermission({ permission: 'trails:manage' });
  const isPLP = hasPermission({ permission: 'public_lands:manage' });

  const languageRegionCode = useLanguageRegionCode();
  const [isPopupHovered, setIsPopupHovered] = useState(false);
  const [currentResult, setCurrentResult] = useState(hoveredAdminResult);
  const [showPopup, setShowPopup] = useState(true);
  const { mapId, trailId } = useMemo(() => {
    if (!currentResult) {
      return {};
    }

    return {
      mapId: currentResult.type !== 'trail' ? currentResult.ID : undefined,
      trailId: currentResult.type === 'trail' ? currentResult.ID : undefined
    };
  }, [currentResult]);
  const { data: polylineCoordinates } = useGetPolylineQuery({ baseUrl, mapId, trailId }, { skip: !mapId && !trailId });

  useEffect(() => {
    if (hoveredAdminResult) {
      setCurrentResult(hoveredAdminResult);
    } else if (!isPopupHovered) {
      setCurrentResult(undefined);
    }
  }, [hoveredAdminResult, isPopupHovered]);

  const openContributeLink = useCallback(() => {
    window.open(`/contribute/route?trail_id=${currentResult?.ID}`, '_blank');
  }, [currentResult]);

  useEffect(() => {
    if (!map) {
      return;
    }
    const onMouseMove = (event: MapMouseEvent) => {
      setShowPopup(!event.originalEvent?.shiftKey);
    };
    map?.on('mousemove', onMouseMove);
    return () => {
      map?.off('mousemove', onMouseMove);
    };
  }, [map]);

  return (
    <>
      {currentResult?._geoloc && showPopup && !currentResult.suppressPopup && (
        <Popup
          closeButton={false}
          closeOnClick={false}
          focusAfterOpen={false}
          latitude={currentResult._geoloc.lat}
          longitude={currentResult._geoloc.lng}
          maxWidth="none"
          offset={currentResult.popupOffset || [0, -16]}
        >
          {currentResult.type === 'trail' && (
            <TrailCard
              languageRegionCode={languageRegionCode}
              onCardMouseEnter={() => setIsPopupHovered(true)}
              onCardMouseLeave={() => setIsPopupHovered(false)}
              variant="small"
              trail={trailHitToTrail(currentResult as TrailHit)}
              isFavorite={listItemsProps?.isFavorite('trail', currentResult.ID)}
              onFavoriteClick={listItemsProps ? () => listItemsProps.onFavoriteClick('trail', currentResult.ID) : undefined}
              adminCTAs={
                isAdmin || isPLP
                  ? [
                      {
                        label: 'Edit',
                        id: 'edit',
                        onClick: openContributeLink
                      }
                    ]
                  : undefined
              }
              openInNewTab
            />
          )}
          {currentResult.type === 'track' && <ActivityCard currentResult={currentResult as AdminTrackHit} setIsPopupHovered={setIsPopupHovered} />}
          {currentResult.type === 'map' && <MapCard setIsPopupHovered={setIsPopupHovered} currentResult={currentResult as AdminMapHit} />}
        </Popup>
      )}

      {polylineCoordinates && currentResult && <Polyline coordinates={polylineCoordinates} uniqueId="hovered-map" />}
      {listItemsProps?.listModal}
      {listItemsProps?.signUpModal}
    </>
  );
};

export default HoveredResultCardAndPolyline;
